import { toastError } from '@/lib/toast-error';
import { trpc } from '@/lib/trpc';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

// ── Access ────────────────────────────────────────────────────────────────────

export type EditScope = 'all' | 'cases' | 'faq';

/** Teams viewer base — imborrables desde el panel (espejo del backend). */
export const BASE_VIEWER_TEAMS = ['Partnerships & Marketing', 'Creative Studio', 'Business'];

export function usePartnershipsAccess() {
  return trpc.partnerships.checkAccess.useQuery(undefined, {
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Espejo cliente de `hasEditScope` del backend: true si el usuario puede editar
 * la sección dada (tiene scope 'all' o el scope de la sección). Solo oculta
 * botones — la defensa real está en las mutations del backend.
 */
export function canEditSection(
  editScopes: readonly EditScope[] | undefined,
  section: EditScope
): boolean {
  if (!editScopes) return false;
  return editScopes.includes('all') || editScopes.includes(section);
}

// ── Governance de Permisos (panel admin) ──────────────────────────────────────

export function usePartnershipsGovernance() {
  return trpc.partnerships.governance.getGovernance.useQuery(undefined, { retry: false });
}

export function useCommunityPicker() {
  return trpc.partnerships.governance.listCommunityForPicker.useQuery(undefined, {
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
}

export function useSaveGovernance() {
  const { t } = useTranslation();
  const utils = trpc.useUtils();
  return trpc.partnerships.governance.setGovernance.useMutation({
    onSuccess: () => {
      utils.partnerships.governance.getGovernance.invalidate();
      utils.partnerships.checkAccess.invalidate();
      utils.profile.getVisibility.invalidate();
      toast.success(t('hooks.partnerships.permisos_guardados'));
    },
    onError: (err) => toastError(err, 'common.toast.error_al_guardar_permisos'),
  });
}

// ── Partners ──────────────────────────────────────────────────────────────────
// Solo lectura desde el front: los 3 partners (tiktok/google/meta) están fijos por
// el enum del backend y por PARTNER_CONFIG, así que dar de alta uno nuevo es un
// cambio de código. Los endpoints createPartner/updatePartner siguen existiendo.
// ──────────────────────────────────────────────────────────────────

export function usePartners() {
  return trpc.partnerships.getPartners.useQuery();
}

// ── Products ──────────────────────────────────────────────────────────────────

export function useProducts(partnerId: string, category?: string) {
  return trpc.partnerships.getProducts.useQuery(
    { partnerId, category: category as any },
    { enabled: !!partnerId }
  );
}

export function useProduct(productId: string) {
  return trpc.partnerships.getProduct.useQuery(productId, { enabled: !!productId });
}

export function useCreateProduct() {
  const { t } = useTranslation();
  const utils = trpc.useUtils();
  return trpc.partnerships.createProduct.useMutation({
    onSuccess: () => {
      utils.partnerships.getProducts.invalidate();
      toast.success(t('hooks.partnerships.producto_creado'));
    },
    onError: (err) => toastError(err, 'common.toast.error_al_crear_producto'),
  });
}

export function useUpdateProduct() {
  const { t } = useTranslation();
  const utils = trpc.useUtils();
  return trpc.partnerships.updateProduct.useMutation({
    onSuccess: () => {
      utils.partnerships.getProducts.invalidate();
      utils.partnerships.getProduct.invalidate();
      toast.success(t('hooks.partnerships.producto_actualizado'));
    },
    onError: (err) => toastError(err, 'common.toast.error_al_actualizar_producto'),
  });
}

// ── Programs ──────────────────────────────────────────────────────────────────

export function usePrograms(partnerId: string) {
  return trpc.partnerships.getPrograms.useQuery(partnerId, { enabled: !!partnerId });
}

export function useProgram(programId: string) {
  return trpc.partnerships.getProgram.useQuery(programId, { enabled: !!programId });
}

export function useCreateProgram() {
  const { t } = useTranslation();
  const utils = trpc.useUtils();
  return trpc.partnerships.createProgram.useMutation({
    onSuccess: () => {
      utils.partnerships.getPrograms.invalidate();
      toast.success(t('hooks.partnerships.programa_creado'));
    },
    onError: (err) => toastError(err, 'common.toast.error_al_crear_programa'),
  });
}

export function useUpdateProgram() {
  const { t } = useTranslation();
  const utils = trpc.useUtils();
  return trpc.partnerships.updateProgram.useMutation({
    onSuccess: () => {
      utils.partnerships.getPrograms.invalidate();
      utils.partnerships.getProgram.invalidate();
      toast.success(t('hooks.partnerships.programa_actualizado'));
    },
    onError: (err) => toastError(err, 'common.toast.error_al_actualizar_programa'),
  });
}

// ── Packages ──────────────────────────────────────────────────────────────────

export function usePackages(partnerId: string) {
  return trpc.partnerships.getPackages.useQuery(partnerId, { enabled: !!partnerId });
}

/** Archivados de un partner: solo se piden cuando se abre la vista "Archivados". */
export function useArchivedPackages(partnerId: string, enabled: boolean) {
  return trpc.partnerships.getArchivedPackages.useQuery(partnerId, { enabled: !!partnerId && enabled });
}

export function usePackage(packageId: string) {
  return trpc.partnerships.getPackage.useQuery(packageId, { enabled: !!packageId });
}

export function useCreatePackage() {
  const { t } = useTranslation();
  const utils = trpc.useUtils();
  return trpc.partnerships.createPackage.useMutation({
    onSuccess: () => {
      utils.partnerships.getPackages.invalidate();
      toast.success(t('hooks.partnerships.paquete_creado'));
    },
    onError: (err) => toastError(err, 'common.toast.error_al_crear_paquete'),
  });
}

export function useUpdatePackage() {
  const { t } = useTranslation();
  const utils = trpc.useUtils();
  return trpc.partnerships.updatePackage.useMutation({
    onSuccess: () => {
      utils.partnerships.getPackages.invalidate();
      utils.partnerships.getArchivedPackages.invalidate();
      utils.partnerships.getPackage.invalidate();
      toast.success(t('hooks.partnerships.paquete_actualizado'));
    },
    onError: (err) => {
      // Un CONFLICT (otro lo editó) deja la lista vieja: se refresca para que el reintento use la versión nueva.
      utils.partnerships.getPackages.invalidate();
      utils.partnerships.getArchivedPackages.invalidate();
      toastError(err, 'common.toast.error_al_actualizar_paquete');
    },
  });
}

// ── Cases ─────────────────────────────────────────────────────────────────────

export function useCases(partnerId: string, market?: string) {
  return trpc.partnerships.getCases.useQuery(
    { partnerId, market },
    { enabled: !!partnerId }
  );
}

export function useCase(caseId: string) {
  return trpc.partnerships.getCase.useQuery(caseId, { enabled: !!caseId });
}

export function useCreateCase() {
  const { t } = useTranslation();
  const utils = trpc.useUtils();
  return trpc.partnerships.createCase.useMutation({
    onSuccess: () => {
      utils.partnerships.getCases.invalidate();
      toast.success(t('hooks.partnerships.caso_creado'));
    },
    onError: (err) => toastError(err, 'common.toast.error_al_crear_caso'),
  });
}

export function useUpdateCase() {
  const { t } = useTranslation();
  const utils = trpc.useUtils();
  return trpc.partnerships.updateCase.useMutation({
    onSuccess: () => {
      utils.partnerships.getCases.invalidate();
      utils.partnerships.getCase.invalidate();
      toast.success(t('hooks.partnerships.caso_actualizado'));
    },
    onError: (err) => toastError(err, 'common.toast.error_al_actualizar_caso'),
  });
}

// ── FAQs ──────────────────────────────────────────────────────────────────────

export function useFAQs(partnerId: string) {
  return trpc.partnerships.getFAQs.useQuery(partnerId, { enabled: !!partnerId });
}

export function useCreateFAQ() {
  const { t } = useTranslation();
  const utils = trpc.useUtils();
  return trpc.partnerships.createFAQ.useMutation({
    onSuccess: () => {
      utils.partnerships.getFAQs.invalidate();
      toast.success(t('hooks.partnerships.faq_creada'));
    },
    onError: (err) => toastError(err, 'common.toast.error_al_crear_faq'),
  });
}

export function useUpdateFAQ() {
  const { t } = useTranslation();
  const utils = trpc.useUtils();
  return trpc.partnerships.updateFAQ.useMutation({
    onSuccess: () => {
      utils.partnerships.getFAQs.invalidate();
      toast.success(t('hooks.partnerships.faq_actualizada'));
    },
    onError: (err) => toastError(err, 'common.toast.error_al_actualizar_faq'),
  });
}

// ── Frameworks ────────────────────────────────────────────────────────────────

export function useFrameworks(partnerId: string) {
  return trpc.partnerships.getFrameworks.useQuery(partnerId, { enabled: !!partnerId });
}

export function useFramework(frameworkId: string) {
  return trpc.partnerships.getFramework.useQuery(frameworkId, { enabled: !!frameworkId });
}

export function useCreateFramework() {
  const { t } = useTranslation();
  const utils = trpc.useUtils();
  return trpc.partnerships.createFramework.useMutation({
    onSuccess: () => {
      utils.partnerships.getFrameworks.invalidate();
      toast.success(t('hooks.partnerships.framework_creado'));
    },
    onError: (err) => toastError(err, 'common.toast.error_al_crear_framework'),
  });
}

export function useUpdateFramework() {
  const { t } = useTranslation();
  const utils = trpc.useUtils();
  return trpc.partnerships.updateFramework.useMutation({
    onSuccess: () => {
      utils.partnerships.getFrameworks.invalidate();
      utils.partnerships.getFramework.invalidate();
      toast.success(t('hooks.partnerships.framework_actualizado'));
    },
    onError: (err) => toastError(err, 'common.toast.error_al_actualizar_framework'),
  });
}

// ── Assets ────────────────────────────────────────────────────────────────────

export function useAssets(partnerId: string, entityId?: string) {
  return trpc.partnerships.getAssets.useQuery(
    { partnerId, entityId },
    { enabled: !!partnerId }
  );
}

export function useAsset(assetId: string) {
  return trpc.partnerships.getAsset.useQuery(assetId, { enabled: !!assetId });
}

export function useCreateAsset() {
  const { t } = useTranslation();
  const utils = trpc.useUtils();
  return trpc.partnerships.createAsset.useMutation({
    onSuccess: () => {
      utils.partnerships.getAssets.invalidate();
      toast.success(t('hooks.partnerships.asset_creado'));
    },
    onError: (err) => toastError(err, 'common.toast.error_al_crear_asset'),
  });
}

export function useUpdateAsset() {
  const { t } = useTranslation();
  const utils = trpc.useUtils();
  return trpc.partnerships.updateAsset.useMutation({
    onSuccess: () => {
      utils.partnerships.getAssets.invalidate();
      utils.partnerships.getAsset.invalidate();
      toast.success(t('hooks.partnerships.asset_actualizado'));
    },
    onError: (err) => toastError(err, 'common.toast.error_al_actualizar_asset'),
  });
}

// ── Markets ───────────────────────────────────────────────────────────────────

export function useMarkets() {
  return trpc.partnerships.getMarkets.useQuery(undefined, {
    staleTime: 10 * 60 * 1000,
  });
}

export function useCreateMarket() {
  const { t } = useTranslation();
  const utils = trpc.useUtils();
  return trpc.partnerships.createMarket.useMutation({
    onSuccess: () => {
      utils.partnerships.getMarkets.invalidate();
      toast.success(t('hooks.partnerships.mercado_creado'));
    },
    onError: (err) => toastError(err, 'common.toast.error_al_crear_mercado'),
  });
}

export function useUpdateMarket() {
  const { t } = useTranslation();
  const utils = trpc.useUtils();
  return trpc.partnerships.updateMarket.useMutation({
    onSuccess: () => {
      utils.partnerships.getMarkets.invalidate();
      toast.success(t('hooks.partnerships.mercado_actualizado'));
    },
    onError: (err) => toastError(err, 'common.toast.error_al_actualizar_mercado'),
  });
}

// ── Asistente ─────────────────────────────────────────────────────────────────

export function useAskAssistant() {
  return trpc.partnerships.askAssistant.useMutation({
    onError: (err) => toastError(err, 'common.toast.error_del_asistente'),
  });
}
